import { Box, Theme, Typography } from "@outschool/backpack";
import {
  GradingType,
  MasteryEvaluationType,
  getGradingData,
  getMasteryEvaluationData
} from "@outschool/business-rules";
import { ClassDetailsQuery_ActivityFragment } from "@outschool/gql-frontend-generated";
import {
  IconDefinition,
  faAwardSimple,
  faBooks,
  faCalendarDay,
  faCalendarWeek,
  faClipboardListCheck,
  faClock,
  faCommentPen,
  faFileCertificate,
  faFilePen,
  faLightbulbOn,
  faMessageLines,
  faNotebook,
  faScreenUsers,
  faSignalStream,
  faTrophy
} from "@outschool/icons";
import { useMachineTranslation, useTranslation } from "@outschool/localization";
import {
  useActivityString,
  usePriceDetailsString
} from "@outschool/ui-components-website";
import { Screen } from "@outschool/ui-utils";
import React from "react";

import HeadingMarker from "../subcomponents/HeadingMarker";
import { YellowBlobIcon } from "../subcomponents/IconWithBackground";
import ShowMoreModal from "../subcomponents/ShowMoreModal";

type IncludedItem = {
  show: boolean;
  icon: IconDefinition;
  title: string;
  subtitle?: string;
  masteryEvaluationData?: {
    details?: string;
    frequency?: string;
    asyncTime?: string;
  };
};

export default function Overview({
  activity,
  headerRef,
  showExpanded
}: {
  activity: ClassDetailsQuery_ActivityFragment;
  headerRef?: React.RefObject<HTMLElement>;
  showExpanded?: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\sections\\Overview"
  );
  const { translate } = useMachineTranslation();

  const isMobile = Screen.useIsMobile();

  const { masteryEvaluationType, masteryAndGradingFrequency, gradingType } =
    useActivityString();
  const { classFormatString } = usePriceDetailsString();

  const inClassWeeklyMinutes =
    activity.weekly_meetings && activity.duration_minutes
      ? activity.weekly_meetings * activity.duration_minutes
      : 0;

  const inClassTotalMinutes = activity.duration_weeks
    ? activity.duration_weeks * inClassWeeklyMinutes
    : 0;

  const hours = (minutes: number) => Math.floor(minutes / 60);
  const remainderMinutes = (minutes: number) => Math.round(minutes % 60);
  const hoursAndMinutesString = (minutes: number) => {
    return remainderMinutes(minutes) > 0
      ? hours(minutes) > 0
        ? t("{{hours}} hrs {{mins}} mins", {
            hours: hours(minutes),
            mins: remainderMinutes(minutes)
          })
        : t("{{mins}} mins", {
            mins: remainderMinutes(minutes)
          })
      : hours(minutes);
  };
  const inClassHoursString = activity.is_ongoing_weekly
    ? hoursAndMinutesString(inClassWeeklyMinutes)
    : hoursAndMinutesString(inClassTotalMinutes);

  const lessons = activity.syllabus?.lessons ?? [];

  const selfPacedVideoMinutes =
    lessons.reduce(
      (sum, lesson) => sum + lesson.videoDurationSeconds / 60,
      0
    ) ?? 0;

  const numberLiveMeetings =
    (activity.duration_weeks || 1) * (activity.weekly_meetings || 1);

  const asyncTimeString = activity?.details?.asyncTime
    ? activity.details.asyncTime === "0 - 1"
      ? t("1 hour per week")
      : activity.details.asyncTime === "0"
      ? ""
      : t("{{time}} hours per week", {
          time: activity.details.asyncTime.replaceAll(" ", "")
        })
    : "";

  function getIconForMasteryEvaluation(type: MasteryEvaluationType) {
    switch (type) {
      case MasteryEvaluationType.Homework:
        return faNotebook;
      case MasteryEvaluationType.Projects:
        return faLightbulbOn;
      case MasteryEvaluationType.Assignments:
        return faFilePen;
      default:
        return faNotebook;
    }
  }

  function getIconForGrading(type: GradingType) {
    switch (type) {
      case GradingType.Assessment:
        return faClipboardListCheck;
      case GradingType.LetterGrade:
        return faAwardSimple;
      case GradingType.ProgressReport:
        return faCommentPen;
      case GradingType.CertificateOfCompletion:
        return faFileCertificate;
      default:
        return faTrophy;
    }
  }

  const masteryItems = getMasteryEvaluationData(activity).map(
    ({ type, details, frequency }, index) => ({
      show: true,
      icon: getIconForMasteryEvaluation(type),
      title: masteryEvaluationType({ type, displayTo: "parent" }),
      subtitle:
        (index == 0 && asyncTimeString ? asyncTimeString + ". " : "") +
        (details
          ? translate(details)
          : masteryAndGradingFrequency({ frequency, displayTo: "parent" })),
      masteryEvaluationData: {
        details: translate(details.trim()),
        frequency: masteryAndGradingFrequency({
          frequency,
          displayTo: "parent"
        }),
        asyncTime: index == 0 ? asyncTimeString : undefined
      }
    })
  );

  const gradingItems = getGradingData(activity).map(
    ({ type, details, frequency }) => ({
      show: true,
      icon: getIconForGrading(type),
      title: gradingType({ type, displayTo: "parent" }),
      subtitle: details
        ? translate(details)
        : masteryAndGradingFrequency({ frequency, displayTo: "parent" }),
      masteryEvaluationData: {
        details: translate(details.trim()),
        frequency: masteryAndGradingFrequency({
          frequency,
          displayTo: "parent"
        })
      }
    })
  );

  const selfPacedItems: IncludedItem[] = [
    {
      show: activity.isSelfPaced,
      icon: faBooks,
      title: t("{{number}} pre-recorded lesson", {
        number: lessons.length,
        count: lessons.length,
        defaultValue_plural: "{{number}} pre-recorded lessons"
      }),
      subtitle:
        selfPacedVideoMinutes >= 120
          ? t("average {{mins}} mins per video", {
              mins: Math.round(selfPacedVideoMinutes / lessons.length)
            })
          : undefined
    },
    {
      show: activity.isSelfPaced,
      icon: faMessageLines,
      title: t("{{weeks}} week", {
        weeks: activity.duration_weeks,
        count: activity.duration_weeks ?? 0,
        defaultValue_plural: "{{weeks}} weeks"
      }),
      subtitle: t("of teacher support")
    },
    {
      show:
        activity.isSelfPaced &&
        selfPacedVideoMinutes != null &&
        selfPacedVideoMinutes >= 120,
      icon: faClock,
      title: t("{{hours}} hrs {{minutes}} mins", {
        hours: Math.floor(selfPacedVideoMinutes / 60),
        minutes: Math.round(selfPacedVideoMinutes % 60)
      }),
      subtitle: t("total video learning hours")
    },
    {
      show: activity.isSelfPaced,
      icon: faCalendarWeek,
      title: t("1 year access"),
      subtitle: t("to the content")
    }
  ];

  const tutoringItems: IncludedItem[] = [
    {
      show: activity.hasTeacherSchedule,
      icon: faCalendarDay,
      title: t("Meets on Demand"),
      subtitle: t("schedule meetings as needed")
    },
    {
      show: activity.hasTeacherSchedule,
      icon: faClock,
      title: t("{{minutes}} mins", { minutes: activity.duration_minutes }),
      subtitle: t("per session")
    },
    {
      show: activity.hasTeacherSchedule,
      icon: faMessageLines,
      title: t("Teacher support")
    }
  ];

  const recurringItems: IncludedItem[] = [
    {
      show: activity.is_ongoing_weekly,
      icon: faScreenUsers,
      title: t("{{total}} live meeting", {
        total: numberLiveMeetings,
        count: numberLiveMeetings,
        defaultValue_plural: "{{total}} live meetings"
      }),
      subtitle: t("{{time}} in-class hours per week", {
        time: inClassHoursString
      })
    }
  ];

  const allItems: IncludedItem[] = [
    {
      show: true,
      icon: faSignalStream,
      title: classFormatString(activity),
      subtitle: activity.isSelfPaced
        ? t("Asynchronous learning — no live meetings")
        : t("Meet over live video meetings")
    },
    {
      show:
        !activity.is_ongoing_weekly &&
        activity.duration_weeks != null &&
        activity.duration_weeks > 0 &&
        activity.weekly_meetings != null &&
        activity.weekly_meetings > 0,
      icon: faScreenUsers,
      title: t("{{total}} live meeting", {
        total: numberLiveMeetings,
        count: numberLiveMeetings,
        defaultValue_plural: "{{total}} live meetings"
      }),
      subtitle: t("{{time}} in-class hours", { time: inClassHoursString })
    },
    ...recurringItems,
    ...tutoringItems,
    ...selfPacedItems,
    ...masteryItems,
    ...gradingItems
  ];

  const includedItems = allItems.filter(item => item.show);

  const itemsLimit = isMobile ? 6 : 9;

  // don't show the "show all" button if there's few enough items, unless the items have more details
  const showButton =
    includedItems.length > itemsLimit ||
    includedItems.filter(item => item.masteryEvaluationData).length > 0;

  if (showExpanded) {
    return <Body includedItems={includedItems} isModal />;
  }

  return (
    <Box>
      <Body
        includedItems={includedItems.slice(0, itemsLimit)}
        headerRef={headerRef}
      />
      {showButton && (
        <ShowMoreModal
          buttonLabel={t("Show All {{length}} Details", {
            length: includedItems.length
          })}
          trackingName={"whats_included"}
        >
          <Body includedItems={includedItems} isModal />
        </ShowMoreModal>
      )}
    </Box>
  );
}

function Body({
  includedItems,
  isModal = false,
  headerRef
}: {
  includedItems: IncludedItem[];
  isModal?: boolean;
  headerRef?: React.RefObject<HTMLElement>;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\sections\\Overview"
  );
  function formatFrequencyString(freq: string, title: string) {
    const name = title === "Homework" ? "Homework assignment" : title;
    const lastCharacter = name.charAt(name.length - 1);
    const pluralName =
      name === "Certificate of Completion"
        ? "Certificates of Completion"
        : lastCharacter === "s"
        ? name
        : name + "s";

    if (freq === "included") {
      if (title === "Homework") {
        return t("Homework assignments are included in this class.");
      }
      if (title === "Grading") {
        return t("Grading included after class completion.");
      }
      if (title === "Certificate of Completion") {
        return t("Certificate of completion included after class completion.");
      }
      return pluralName + t(" are included in this class.");
    }

    if (freq.includes("completion")) {
      return freq.replace(" ", " " + name.toLowerCase() + " ");
    }

    return freq.replace(" ", " " + pluralName.toLowerCase() + " ");
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 16
      }}
    >
      <Box>
        {!isModal && <HeadingMarker />}
        <Typography emphasized variant="h4" ref={headerRef} id="overview">
          {t("Overview")}
        </Typography>
      </Box>
      <Box
        sx={(theme: Theme) =>
          isModal
            ? {
                display: "flex",
                flexDirection: "column"
              }
            : {
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: 40,
                rowGap: 24,
                paddingY: 8,
                [theme.breakpoints.down("md")]: {
                  display: "flex",
                  flexDirection: "column",
                  gap: "1em"
                }
              }
        }
      >
        {includedItems.map(item => (
          <Box
            key={item.title}
            sx={{
              display: "flex",
              gap: 8,
              "&:not(:last-child)": {
                borderBottom: isModal ? 1 : undefined,
                borderColor: "grey.200"
              },
              paddingY: isModal ? 16 : 0,
              overflow: "hidden"
            }}
          >
            <YellowBlobIcon icon={item.icon} />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                overflow: "hidden"
              }}
            >
              <Typography variant={isModal ? "h6" : "subtitle1"}>
                {item.title}
              </Typography>
              {(!item.masteryEvaluationData || !isModal) && (
                <Typography
                  variant={isModal ? "subtitle1" : "body2"}
                  color={isModal ? undefined : "grey.600"}
                  sx={{
                    paddingY: isModal ? 8 : 0,
                    overflow: !isModal ? "hidden" : "",
                    textOverflow: !isModal ? "ellipsis" : "",
                    whiteSpace: "nowrap"
                  }}
                >
                  {item.subtitle}
                </Typography>
              )}
              {item.masteryEvaluationData && isModal && (
                <>
                  {item.masteryEvaluationData.asyncTime && (
                    <Box
                      flex
                      sx={{ alignItems: "baseline", mt: 8, flexWrap: "wrap" }}
                    >
                      <Typography variant="subtitle2">
                        {t("Estimated outside of class time:")}&nbsp;
                      </Typography>
                      <Typography variant="body2">
                        {item.masteryEvaluationData.asyncTime}
                      </Typography>
                    </Box>
                  )}
                  {!!item.masteryEvaluationData.details ? (
                    <Typography variant="body1" component="pre" mt={8}>
                      {item.masteryEvaluationData.details}
                    </Typography>
                  ) : null}
                  {!!item.masteryEvaluationData.frequency &&
                  (!item.masteryEvaluationData.details ||
                    item.masteryEvaluationData.frequency !== "included") ? (
                    <Box flex sx={{ alignItems: "baseline", mt: 8 }}>
                      <Typography variant="subtitle2">
                        {formatFrequencyString(
                          item.masteryEvaluationData.frequency,
                          item.title
                        )}
                      </Typography>
                    </Box>
                  ) : null}
                </>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
